import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView, useReducedMotion } from "framer-motion";
import "./index.css";

import pcImg from "../shared/enthoo-pro-2-server-edition-2.webp";
import pcBackImg from "../shared/mes2x-pc-back-hero.webp";
import gpuImg from "../hero-section/blackwell-gpus.webp";
import cpuImg from "../hero-section/threadripper-cpu.webp";

const parts = [
  {
    id: 1,
    name: "GPU",
    image: gpuImg,
    className: "hs-part hs-part-gpu-main"
  },
  {
    id: 3,
    name: "CPU",
    image: cpuImg,
    className: "hs-part hs-part-cpu"
  }
];

const GPU_OPTIONS = [
  "Dual RTX 5080s",
  "Dual RTX 5090s",
  "Dual RTX PRO 4000s",
  "RTX PRO 6000",
  "Dual RTX PRO 6000s"
];

const CPU_OPTIONS = [
  "Ryzen 9 9900X",
  "Ryzen 9 9900X3D",
  "Ryzen 9 9950X",
  "Ryzen 9 9950X3D",
  "Ryzen 7 9800X3D",
  "Ryzen 9 9950X3D2",
  "Threadripper 9955WX",
  "Threadripper 9965WX",
  "Threadripper 9975WX",
  "Threadripper 9985WX",
  "Threadripper 9995WX"
];

const SLOT_INTERVAL_MS = 2400;
const CPU_PHASE_OFFSET_MS = 1200;

export default function App() {
  const springConfig = { type: "spring", stiffness: 400, damping: 28 };
  const rootRef = useRef(null);
  const inView = useInView(rootRef, { margin: "200px 0px" });
  const prefersReducedMotion = useReducedMotion();
  const shouldAnimate = inView && !prefersReducedMotion;

  const [gpuIdx, setGpuIdx] = useState(0);
  const [cpuIdx, setCpuIdx] = useState(0);

  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !inView || prefersReducedMotion) return;
    const parent = canvas.parentElement;
    if (!parent) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    let width = 0;
    let height = 0;
    const isMobile = window.innerWidth < 600;
    const DRIFT_COUNT = isMobile ? 5 : 7;
    const RISER_COUNT = isMobile ? 5 : 8;
    const LINK_DIST = 150;
    const FADE_EDGE = 80;
    const dots = [];

    const sideBiasedX = () => Math.random() * width;

    const spawnRiser = (d, atBottom) => {
      d.x = sideBiasedX();
      d.y = atBottom ? height + 10 : Math.random() * height;
      d.vx = (Math.random() - 0.5) * 0.05;
      d.vy = -(0.2 + Math.random() * 0.4);
      d.radius = 1.7 + Math.random() * 1.1;
    };

    const resize = () => {
      const rect = parent.getBoundingClientRect();
      const oldW = width;
      const oldH = height;
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (dots.length === 0) {
        for (let i = 0; i < DRIFT_COUNT; i++) {
          dots.push({
            type: "drift",
            x: Math.random() * width,
            y: Math.random() * height,
            vx: (Math.random() - 0.5) * 0.17,
            vy: (Math.random() - 0.5) * 0.17,
            radius: 2.2
          });
        }
        for (let i = 0; i < RISER_COUNT; i++) {
          const d = { type: "riser" };
          spawnRiser(d, false);
          dots.push(d);
        }
      } else if (oldW > 0 && oldH > 0) {
        for (const d of dots) {
          d.x = (d.x / oldW) * width;
          d.y = (d.y / oldH) * height;
        }
      }
    };
    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(parent);

    const getAlpha = (d) => {
      if (d.type === "drift") return 1;
      if (d.y < FADE_EDGE) return Math.max(0, d.y / FADE_EDGE);
      if (d.y > height - FADE_EDGE)
        return Math.max(0, (height - d.y) / FADE_EDGE);
      return 1;
    };

    let rafId;
    const tick = () => {
      ctx.clearRect(0, 0, width, height);

      for (const d of dots) {
        d.x += d.vx;
        d.y += d.vy;
        if (d.type === "drift") {
          if (d.x < 0 || d.x > width) d.vx *= -1;
          if (d.y < 0 || d.y > height) d.vy *= -1;
        } else {
          if (d.y < -20) spawnRiser(d, true);
          if (d.x < 0 || d.x > width) d.vx *= -0.6;
        }
      }

      ctx.lineWidth = 1;
      for (let i = 0; i < dots.length; i++) {
        const aA = getAlpha(dots[i]);
        if (aA <= 0) continue;
        for (let j = i + 1; j < dots.length; j++) {
          const aB = getAlpha(dots[j]);
          if (aB <= 0) continue;
          const dx = dots[i].x - dots[j].x;
          const dy = dots[i].y - dots[j].y;
          const distSq = dx * dx + dy * dy;
          const thresholdSq = LINK_DIST * LINK_DIST;
          if (distSq < thresholdSq) {
            const dist = Math.sqrt(distSq);
            const alpha = (1 - dist / LINK_DIST) * 0.28 * Math.min(aA, aB);
            ctx.strokeStyle = `rgba(222, 52, 49, ${alpha})`;
            ctx.beginPath();
            ctx.moveTo(dots[i].x, dots[i].y);
            ctx.lineTo(dots[j].x, dots[j].y);
            ctx.stroke();
          }
        }
      }

      for (const d of dots) {
        const alpha = getAlpha(d);
        if (alpha <= 0) continue;
        ctx.fillStyle = `rgba(222, 52, 49, ${0.55 * alpha})`;
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.radius, 0, Math.PI * 2);
        ctx.fill();
      }

      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [inView, prefersReducedMotion]);

  useEffect(() => {
    if (!shouldAnimate) return;
    const id = setInterval(
      () => setGpuIdx((i) => (i + 1) % GPU_OPTIONS.length),
      SLOT_INTERVAL_MS
    );
    return () => clearInterval(id);
  }, [shouldAnimate]);

  useEffect(() => {
    if (!shouldAnimate) return;
    let intervalId;
    const startId = setTimeout(() => {
      setCpuIdx((i) => (i + 1) % CPU_OPTIONS.length);
      intervalId = setInterval(
        () => setCpuIdx((i) => (i + 1) % CPU_OPTIONS.length),
        SLOT_INTERVAL_MS
      );
    }, CPU_PHASE_OFFSET_MS);
    return () => {
      clearTimeout(startId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [shouldAnimate]);

  const slotTransition = { duration: 0.45, ease: [0.4, 0, 0.2, 1] };

  return (
    <>
    <main ref={rootRef} className={`hs-hero${shouldAnimate ? "" : " hs-paused"}`}>
      <div className="hs-grid" />
      <div className="hs-glow" />
      <canvas ref={canvasRef} className="hs-constellation" aria-hidden="true" />

      <div className="hs-brackets" aria-hidden="true">
        <span className="hs-bracket hs-bracket-tl" />
        <span className="hs-bracket hs-bracket-tr" />
        <span className="hs-bracket hs-bracket-bl" />
        <span className="hs-bracket hs-bracket-br" />
      </div>

      <div className="hs-plus" aria-hidden="true" />

      <div className="hs-hero-inner">
        <div className="hs-text-col">
          <motion.span
            className="hs-eyebrow"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <span className="hs-eyebrow-dot" />
            Precision AI Workstations
          </motion.span>

          <motion.h1
            className="hs-hero-title"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.25 }}
          >
            <span className="hs-hero-title-main">
              Built for <span className="hs-accent">AI</span>
            </span>
            <span className="hs-hero-title-sub">
              Machine Learning &amp; Local LLM Workstations
            </span>
          </motion.h1>

          <motion.div
            className="hs-hero-buttons"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <a href="#customize" className="hs-hero-btn hs-hero-btn-primary">
              Customize <span>→</span>
            </a>
            <a href="https://arsenalpc.com/shop/ai-workstations/" className="hs-hero-btn hs-hero-btn-secondary">
              Shop Workstations
            </a>
          </motion.div>

          <motion.ul
            className="hs-spec-strip"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            aria-label="Core hardware"
          >
            <li>
              <span className="hs-spec-label">GPU</span>
              <span className="hs-spec-value hs-slot hs-slot-gpu" aria-live="polite">
                <AnimatePresence initial={false}>
                  <motion.span
                    key={gpuIdx}
                    className="hs-slot-item"
                    initial={{ y: "100%", opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: "-100%", opacity: 0 }}
                    transition={slotTransition}
                  >
                    {GPU_OPTIONS[gpuIdx]}
                  </motion.span>
                </AnimatePresence>
              </span>
            </li>
            <li>
              <span className="hs-spec-label">CPU</span>
              <span className="hs-spec-value hs-slot hs-slot-cpu" aria-live="polite">
                <AnimatePresence initial={false}>
                  <motion.span
                    key={cpuIdx}
                    className="hs-slot-item"
                    initial={{ y: "100%", opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: "-100%", opacity: 0 }}
                    transition={slotTransition}
                  >
                    {CPU_OPTIONS[cpuIdx]}
                  </motion.span>
                </AnimatePresence>
              </span>
            </li>
            <li>
              <span className="hs-spec-label">Cooling</span>
              <span className="hs-spec-value">Liquid-Cooled</span>
            </li>
          </motion.ul>
        </div>

        <div className="hs-hardware-cluster">
          <motion.div
            className="hs-pc hs-pc-back"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.45 }}
          >
            <motion.div
              className="hs-pc-inner"
              initial={{ scale: 0.88 }}
              animate={{ scale: 1 }}
              whileHover={{ scale: 1.06 }}
              transition={{ scale: springConfig }}
            >
              <img src={pcBackImg} alt="AI workstation back" className="hs-pc-image" />
            </motion.div>
          </motion.div>

          <motion.div
            className="hs-pc hs-pc-front"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.55 }}
          >
            <motion.div
              className="hs-pc-inner"
              initial={{ scale: 0.88 }}
              animate={{ scale: 1 }}
              whileHover={{ scale: 1.06 }}
              transition={{ scale: springConfig }}
            >
              <img src={pcImg} alt="AI workstation" className="hs-pc-image" />
            </motion.div>
          </motion.div>

          {parts.filter(part => part.id === 1).map((part) => (
            <motion.div
              key={part.id}
              className={part.className}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.75 }}
            >
              <motion.div
                className="hs-part-inner"
                initial={{ scale: 0.85 }}
                animate={{ scale: 1 }}
                whileHover={{ scale: 1.1 }}
                transition={{ scale: springConfig }}
              >
                <img src={part.image} alt={part.name} className="hs-part-image" />
              </motion.div>
            </motion.div>
          ))}
        </div>

        {parts.filter(part => part.id === 3).map((part) => (
          <motion.div
            key={part.id}
            className={part.className}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.85 }}
          >
            <motion.div
              className="hs-part-inner"
              initial={{ scale: 0.85 }}
              animate={{ scale: 1 }}
              whileHover={{ scale: 1.1 }}
              transition={{ scale: springConfig }}
            >
              <img src={part.image} alt={part.name} className="hs-part-image" />
            </motion.div>
          </motion.div>
        ))}
      </div>
    </main>
    <div className="hs-trust-bar">
      <p className="hs-trust-heading">
        <span className="hs-trust-heading-accent">Trusted</span> By Professionals at:
      </p>
    </div>
    </>
  );
}
